// Star WebPRNT client for Star Micronics LAN printers (e.g. TSP100IIILAN).
// Sends XML directly to the printer's built-in HTTP endpoint:
//   POST http://<PRINTER_IP>/StarWebPRNT/SendMessage
// Bypasses the OS print dialog entirely (no more A4 prompt on tablets).
//
// Docs ref: https://www.star-m.jp/products/s_print/sdk/webprnt/manual/en/

const LINE_WIDTH = 48; // 80mm paper, font A → 48 chars per line

const escapeXml = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

// Build a printable text-line bounded to LINE_WIDTH chars with optional right-aligned price.
const padLine = (left, right = "") => {
  const l = String(left ?? "");
  const r = String(right ?? "");
  const space = Math.max(1, LINE_WIDTH - l.length - r.length);
  return l + " ".repeat(space) + r;
};

const center = (s) => {
  const t = String(s ?? "");
  if (t.length >= LINE_WIDTH) return t;
  const pad = Math.floor((LINE_WIDTH - t.length) / 2);
  return " ".repeat(pad) + t;
};

const HR = "-".repeat(LINE_WIDTH);
const HR_DBL = "=".repeat(LINE_WIDTH);

// Fold long text onto multiple lines, indenting continuation lines.
const fold = (text, width = LINE_WIDTH, indent = "") => {
  const out = [];
  const words = String(text ?? "").split(/\s+/);
  let line = "";
  for (const w of words) {
    if (!w) continue;
    if ((line ? line.length + 1 : 0) + w.length <= width) {
      line = line ? line + " " + w : w;
    } else {
      if (line) out.push(line);
      line = indent + w;
    }
  }
  if (line) out.push(line);
  return out.length ? out : [""];
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-CH") + " " + d.toLocaleTimeString("fr-CH", { hour: "2-digit", minute: "2-digit" });
  } catch (e) {
    return String(iso);
  }
};

// Build the Star WebPRNT XML body for a receipt order.
export const buildStarReceiptXml = (order, { autoCut = true } = {}) => {
  const parts = [];
  parts.push('<?xml version="1.0" encoding="UTF-8"?>');
  parts.push("<root>");

  // Header — big & bold, centered
  parts.push('<alignment position="center"/>');
  parts.push('<bold>');
  parts.push(`<text>${escapeXml(center("QUANG"))}&#10;</text>`);
  parts.push('</bold>');
  parts.push(`<text>${escapeXml(center("Restaurant Vietnamien - Lausanne"))}&#10;</text>`);
  parts.push(`<text>${escapeXml(center("Rue Pré-du-Marché 15 - 1004"))}&#10;</text>`);
  parts.push(`<text>${escapeXml(center("Tel : 078 616 29 29"))}&#10;</text>`);
  parts.push(`<text>${HR}&#10;</text>`);

  // Ticket meta — left aligned
  parts.push('<alignment position="left"/>');
  parts.push('<bold>');
  parts.push(`<text>${escapeXml(padLine("TICKET", "#" + (order.order_number || "")))}&#10;</text>`);
  parts.push('</bold>');
  parts.push(`<text>${escapeXml(padLine("Date", fmtDate(order.created_at)))}&#10;</text>`);
  const typeLabel = order.order_type === "delivery" ? "LIVRAISON" : "A EMPORTER";
  parts.push(`<text>${escapeXml(padLine("Type", typeLabel))}&#10;</text>`);

  if (order.is_next_day) {
    parts.push(`<text>${HR}&#10;</text>`);
    parts.push('<bold>');
    parts.push(`<text>${escapeXml(center("** POUR DEMAIN **"))}&#10;</text>`);
    if (order.scheduled_date) parts.push(`<text>${escapeXml(center(order.scheduled_date))}&#10;</text>`);
    if (order.scheduled_time && order.scheduled_time !== "asap") parts.push(`<text>${escapeXml(center(order.scheduled_time))}&#10;</text>`);
    parts.push('</bold>');
  } else if (order.scheduled_time && order.scheduled_time !== "asap") {
    parts.push(`<text>${escapeXml(padLine("Creneau", order.scheduled_time))}&#10;</text>`);
  }

  parts.push(`<text>${HR}&#10;</text>`);

  // Client
  parts.push('<bold>');
  parts.push(`<text>CLIENT&#10;</text>`);
  parts.push('</bold>');
  parts.push(`<text>${escapeXml(`${order.first_name || ""} ${order.last_name || ""}`.trim())}&#10;</text>`);
  if (order.phone) parts.push(`<text>${escapeXml(order.phone)}&#10;</text>`);
  if (order.email) parts.push(`<text>${escapeXml(order.email)}&#10;</text>`);

  if (order.order_type === "delivery") {
    parts.push(`<text>${HR}&#10;</text>`);
    parts.push('<bold>');
    parts.push(`<text>LIVRAISON&#10;</text>`);
    parts.push('</bold>');
    if (order.address) parts.push(`<text>${escapeXml(order.address)}&#10;</text>`);
    if (order.postal_code) parts.push(`<text>${escapeXml(order.postal_code)}&#10;</text>`);
  }

  if (order.note) {
    parts.push(`<text>${HR}&#10;</text>`);
    parts.push('<bold>');
    parts.push(`<text>NOTE COMMANDE&#10;</text>`);
    parts.push('</bold>');
    for (const ln of fold(order.note, LINE_WIDTH)) {
      parts.push(`<text>${escapeXml(ln)}&#10;</text>`);
    }
  }

  // Items
  parts.push(`<text>${HR_DBL}&#10;</text>`);
  const items = Array.isArray(order.items) ? order.items : [];
  for (const it of items) {
    const qty = Number(it.quantity || 1);
    const unit = Number(it.price_each ?? it.unit_price ?? it.price ?? 0);
    const lineTotal = (unit * qty).toFixed(2);
    const name = `${qty}x ${it.name || ""}`;
    // First line: name + total
    const nameLines = fold(name, LINE_WIDTH - 8); // leave room for price
    parts.push('<bold>');
    parts.push(`<text>${escapeXml(padLine(nameLines[0], lineTotal))}&#10;</text>`);
    parts.push('</bold>');
    for (let i = 1; i < nameLines.length; i++) {
      parts.push(`<text>${escapeXml(nameLines[i])}&#10;</text>`);
    }
    // Addons
    const addons = Array.isArray(it.addons) ? it.addons : [];
    for (const a of addons) {
      const an = a.name || a.label || "";
      const ap = Number(a.price || 0);
      const right = ap > 0 ? `+${ap.toFixed(2)}` : "";
      for (const ln of fold("  + " + an, LINE_WIDTH - 8, "    ")) {
        parts.push(`<text>${escapeXml(padLine(ln, right))}&#10;</text>`);
        // only show price on first line of addon
        if (right) break;
      }
    }
    // Per-item note
    if (it.note) {
      for (const ln of fold("  >> " + it.note, LINE_WIDTH, "     ")) {
        parts.push(`<text>${escapeXml(ln)}&#10;</text>`);
      }
    }
  }

  parts.push(`<text>${HR}&#10;</text>`);

  // Totals
  const subtotal = Number(order.items_subtotal != null ? order.items_subtotal : (order.subtotal || 0));
  parts.push(`<text>${escapeXml(padLine("Sous-total", subtotal.toFixed(2)))}&#10;</text>`);
  const discount = Number(order.discount_total || 0);
  if (discount > 0) {
    const promos = Array.isArray(order.applied_promotions) && order.applied_promotions.length > 0
      ? " - " + order.applied_promotions.join(", ")
      : "";
    parts.push('<bold>');
    parts.push(`<text>${escapeXml(padLine("Remise" + promos, "-" + discount.toFixed(2)))}&#10;</text>`);
    parts.push('</bold>');
  }
  if (order.order_type === "delivery") {
    parts.push(`<text>${escapeXml(padLine("Livraison", Number(order.delivery_fee || 0).toFixed(2)))}&#10;</text>`);
  }
  parts.push(`<text>${HR}&#10;</text>`);

  // Grand total — double size + bold
  parts.push('<bold>');
  parts.push('<expanded width="2" height="2">');
  parts.push(`<text>${escapeXml(padLine("TOTAL", Number(order.total || 0).toFixed(2) + " CHF").slice(0, 24))}&#10;</text>`);
  parts.push('</expanded>');
  parts.push('</bold>');

  parts.push(`<text>${HR}&#10;</text>`);
  parts.push('<alignment position="center"/>');
  parts.push(`<text>${escapeXml(center("Paiement sur place / a la livraison"))}&#10;</text>`);
  parts.push(`<text>${escapeXml(center("Merci et a bientot !"))}&#10;</text>`);
  parts.push(`<text>&#10;</text>`);
  parts.push('<bold>');
  parts.push(`<text>${escapeXml(center("- QUANG -"))}&#10;</text>`);
  parts.push('</bold>');
  parts.push(`<feed line="3"/>`);

  if (autoCut) {
    parts.push('<cutpaper feed="true" type="partial"/>');
  }
  parts.push("</root>");
  return parts.join("");
};

// Send XML to the Star WebPRNT endpoint.
// Returns { ok, status, message }.
export const sendToStarPrinter = async ({ ip, xml, timeoutMs = 8000, protocol }) => {
  if (!ip) return { ok: false, status: 0, message: "Adresse IP imprimante manquante" };
  // Choose protocol: try same protocol as page first, then fall back automatically.
  const isHttps = typeof window !== "undefined" && window.location && window.location.protocol === "https:";
  const tryProtos = protocol ? [protocol] : (isHttps ? ["https", "http"] : ["http"]);
  let lastErr = null;
  for (const proto of tryProtos) {
    const url = `${proto}://${ip}/StarWebPRNT/SendMessage`;
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), timeoutMs);
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "text/xml; charset=utf-8" },
        body: xml,
        signal: ctrl.signal,
        // Star printer doesn't set CORS by default; the browser will block reading the
        // response but the printer still receives + prints the payload.
        mode: "no-cors",
      });
      clearTimeout(t);
      return { ok: true, status: res.status || 0, message: "Envoyé à l'imprimante" };
    } catch (e) {
      lastErr = e;
    }
  }
  const isMixed = isHttps;
  return {
    ok: false,
    status: 0,
    message:
      (lastErr && lastErr.message) ||
      (isMixed
        ? "Impossible de joindre l'imprimante. Si vous êtes en HTTPS, autorisez le contenu non sécurisé dans les paramètres du site."
        : "Impossible de joindre l'imprimante. Vérifiez qu'elle est allumée et sur le même réseau."),
  };
};

// Convenience: print a receipt order directly. Returns the same shape as sendToStarPrinter.
export const printOrderToStar = async (order, settings) => {
  const ip = (settings && settings.printer_ip) || "";
  const autoCut = settings ? settings.printer_auto_cut !== false : true;
  const xml = buildStarReceiptXml(order, { autoCut });
  return sendToStarPrinter({ ip, xml });
};

// Test print — short receipt for the "Test impression" button.
export const buildTestXml = () => {
  return (
    '<?xml version="1.0" encoding="UTF-8"?>' +
    "<root>" +
    '<alignment position="center"/>' +
    "<bold>" +
    `<text>${escapeXml(center("TEST QUANG"))}&#10;</text>` +
    "</bold>" +
    `<text>${escapeXml(center("Imprimante OK !"))}&#10;</text>` +
    `<text>${escapeXml(center(new Date().toLocaleString("fr-CH")))}&#10;</text>` +
    '<feed line="3"/>' +
    '<cutpaper feed="true" type="partial"/>' +
    "</root>"
  );
};
